import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchLogs, createLog, deleteLog, fetchMacroGoals, createMacroGoals, updateMacroGoals } from '../lib/tracker-api';
import {
  FaDrumstickBite, FaSmile, FaWeight, FaBan, FaPlus, FaStickyNote, FaTint,
  FaFire, FaTrashAlt, FaChevronLeft, FaChevronRight, FaCalendarDay, FaBolt,
  FaBullseye, FaChartBar, FaClipboardList, FaArrowUp, FaArrowDown, FaCheckCircle,
  FaButter, IconType,
} from '../lib/icons';

const moodEmojis: Record<string, string> = {
  Exhausted: '😫', Drained: '😩', Low: '😔', Sluggish: '😐', Neutral: '😶',
  Balanced: '🙂', Energized: '😊', Vibrant: '😄', Thriving: '🤩', Ecstatic: '🥳',
};
const moodLevels = ['Exhausted', 'Drained', 'Low', 'Sluggish', 'Neutral', 'Balanced', 'Energized', 'Vibrant', 'Thriving', 'Ecstatic'];

const logTypeIcon: Record<string, IconType> = {
  'Macro Log': FaDrumstickBite,
  'Mood Log': FaSmile,
  'Weight Track': FaWeight,
  'Eliminated Item': FaBan,
  'Added Item': FaPlus,
  'Daily Note': FaStickyNote,
  'Period Log': FaTint,
};
const LogTypeIcon = ({ type, className }: { type: string; className?: string }) => {
  const I = logTypeIcon[type] || FaStickyNote;
  return <I className={className} />;
};

const logTypeColors: Record<string, { bg: string; ring: string; text: string; dot: string }> = {
  'Macro Log':       { bg: 'bg-red-50',    ring: 'ring-red-200',    text: 'text-red-700',    dot: 'bg-red-500' },
  'Mood Log':        { bg: 'bg-purple-50', ring: 'ring-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  'Weight Track':    { bg: 'bg-blue-50',   ring: 'ring-blue-200',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  'Eliminated Item': { bg: 'bg-orange-50', ring: 'ring-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
  'Added Item':      { bg: 'bg-green-50',  ring: 'ring-green-200',  text: 'text-green-700',  dot: 'bg-green-500' },
  'Daily Note':      { bg: 'bg-yellow-50', ring: 'ring-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  'Period Log':      { bg: 'bg-pink-50',   ring: 'ring-pink-200',   text: 'text-pink-700',   dot: 'bg-pink-500' },
};

const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
const periodFlows = ['Heavy', 'Medium', 'Light', 'Spotting'];

interface Props { isLoggedIn: boolean; }

/* ───── Circular progress ring ───── */
const Ring = ({ pct, color, size = 72, stroke = 7, children }: { pct: number; color: string; size?: number; stroke?: number; children?: React.ReactNode }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(pct, 100) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth={stroke} fill="none" className="text-gray-100" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
};

export default function Tracker({ isLoggedIn }: Props) {
  const [activeTab, setActiveTab] = useState('log');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthLogs, setMonthLogs] = useState<any[]>([]);
  const [dayLogs, setDayLogs] = useState<any[]>([]);
  const [goals, setGoals] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [addType, setAddType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [itemName, setItemName] = useState('');
  const [notes, setNotes] = useState('');

  const [macroMealType, setMacroMealType] = useState('Breakfast');
  const [macroProtein, setMacroProtein] = useState('');
  const [macroFat, setMacroFat] = useState('');
  const [macroCalories, setMacroCalories] = useState('');

  const [moodRating, setMoodRating] = useState('Neutral');
  const [energyLevel, setEnergyLevel] = useState(5);

  const [weight, setWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('kg');

  const [periodFlow, setPeriodFlow] = useState('');
  const [startPeriod, setStartPeriod] = useState(false);

  const [editGoals, setEditGoals] = useState(false);
  const [goalProtein, setGoalProtein] = useState('');
  const [goalFat, setGoalFat] = useState('');
  const [goalCalories, setGoalCalories] = useState('');

  useEffect(() => { if (isLoggedIn) { loadMonth(); loadGoals(); } }, [isLoggedIn, currentMonth]);
  useEffect(() => { if (isLoggedIn) loadDay(); }, [isLoggedIn, selectedDate]);

  const loadMonth = async () => {
    setLoading(true);
    const [y, m] = currentMonth.split('-');
    const days = new Date(parseInt(y), parseInt(m), 0).getDate();
    try { setMonthLogs(await fetchLogs(`${currentMonth}-01`, `${currentMonth}-${days}`)); } catch {}
    finally { setLoading(false); }
  };
  const loadDay = async () => { try { setDayLogs(await fetchLogs(selectedDate, selectedDate)); } catch {} };
  const loadGoals = async () => {
    try {
      const g = await fetchMacroGoals();
      setGoals(g);
      if (g) { setGoalProtein(String(g.daily_protein_goal || '')); setGoalFat(String(g.daily_fat_goal || '')); setGoalCalories(String(g.daily_calorie_goal || '')); }
    } catch {}
  };

  const handleSaveGoals = async () => {
    try {
      const data = { daily_protein_goal: parseFloat(goalProtein) || 0, daily_fat_goal: parseFloat(goalFat) || 0, daily_calorie_goal: parseFloat(goalCalories) || 0 };
      if (goals?.documentId) await updateMacroGoals(goals.documentId, data);
      else await createMacroGoals(data);
      setEditGoals(false); loadGoals();
    } catch {}
  };

  const handleAddEntry = async () => {
    if (!addType) return;
    setSaving(true);
    try {
      const data: any = { date: selectedDate, log_type: addType };
      switch (addType) {
        case 'Eliminated Item':
        case 'Added Item':
          if (!itemName.trim()) { alert('Please enter an item name.'); setSaving(false); return; }
          data.Item_name = itemName.trim(); data.notes = notes || null; break;
        case 'Daily Note':
          if (!notes.trim()) { alert('Please enter a note.'); setSaving(false); return; }
          data.notes = notes.trim(); break;
        case 'Mood Log':
          data.mood_rating = moodRating; data.energy_level = energyLevel; data.notes = notes || null; break;
        case 'Weight Track':
          const w = parseFloat(weight);
          if (!w || w <= 0) { alert('Please enter a valid weight.'); setSaving(false); return; }
          data.weight_kg = weightUnit === 'kg' ? w : w / 2.20462; data.notes = notes || null; break;
        case 'Period Log':
          data.Item_name = periodFlow || null; data.notes = notes || null;
          if (startPeriod) data.period_start_date = selectedDate; break;
        case 'Macro Log':
          data.protein = parseFloat(macroProtein) || 0; data.fat = parseFloat(macroFat) || 0;
          data.calories = parseFloat(macroCalories) || 0; data.meal_type = macroMealType; data.notes = notes || null; break;
      }
      await createLog(data);
      resetForm(); loadDay(); loadMonth();
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (log: any) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await deleteLog(log.documentId || log.id); loadDay(); loadMonth(); } catch {}
  };

  const resetForm = () => {
    setAddType(null); setItemName(''); setNotes('');
    setMacroProtein(''); setMacroFat(''); setMacroCalories(''); setMacroMealType('Breakfast');
    setMoodRating('Neutral'); setEnergyLevel(5);
    setWeight(''); setPeriodFlow(''); setStartPeriod(false);
  };

  // Memoized derivations — must run before early return for hook order stability
  const streak = useMemo(() => {
    const datesWithLogs = new Set(monthLogs.map((l: any) => l.date));
    let count = 0;
    const d = new Date();
    for (let i = 0; i < 60; i++) {
      const iso = d.toISOString().slice(0, 10);
      if (datesWithLogs.has(iso)) {
        count++;
        d.setDate(d.getDate() - 1);
      } else {
        if (i === 0) { d.setDate(d.getDate() - 1); continue; }
        break;
      }
    }
    return count;
  }, [monthLogs]);

  const daysTrackedThisMonth = useMemo(() => new Set(monthLogs.map((l: any) => l.date)).size, [monthLogs]);

  const trendData = useMemo(() => {
    const days: string[] = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      days.push(d.toISOString().slice(0, 10));
    }
    const caloriesByDay = days.map((iso) => {
      const sum = monthLogs.filter((l: any) => l.date === iso && l.log_type === 'Macro Log').reduce((s: number, l: any) => s + (l.calories || 0), 0);
      return { iso, value: sum };
    });
    const moodScale: Record<string, number> = {
      Exhausted: 1, Drained: 2, Low: 3, Sluggish: 4, Neutral: 5, Balanced: 6, Energized: 7, Vibrant: 8, Thriving: 9, Ecstatic: 10,
    };
    const moodByDay = days.map((iso) => {
      const log = monthLogs.find((l: any) => l.date === iso && l.log_type === 'Mood Log');
      return { iso, value: log ? moodScale[log.mood_rating] || 0 : 0 };
    });
    return { days, caloriesByDay, moodByDay };
  }, [monthLogs]);

  /* ───── Logged-out marketing view ───── */
  if (!isLoggedIn) return (
    <div className="min-h-screen bg-gray-50 overflow-hidden">
      <Helmet>
        <title>Health Tracker — EatMeat | Macros, Weight, Mood & Elimination</title>
        <meta name="description" content="Track daily macros, weight, mood, and elimination diet on your carnivore or ketovore journey. Progress rings, charts, and goals — free forever." />
        <link rel="canonical" href="https://www.eatmeatapp.com/tracker" />
      </Helmet>
      <style>{`
        @keyframes floatUp { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-14px) rotate(3deg); } }
        @keyframes floatUpAlt { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-18px) rotate(-4deg); } }
        @keyframes fadeSlide { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes drawRing { 0% { stroke-dashoffset: 283; } 100% { stroke-dashoffset: 70; } }
        @keyframes barGrow { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes pulse-slow { 0%,100% { opacity: 0.3; } 50% { opacity: 0.6; } }
        @keyframes spin-slow { to { transform: rotate(360deg); } }
        .animate-float { animation: floatUp 5s ease-in-out infinite; }
        .animate-float-alt { animation: floatUpAlt 6.5s ease-in-out infinite; }
        .fade-slide { animation: fadeSlide 0.7s ease-out backwards; }
        .shimmer-text { background: linear-gradient(90deg, #f97316 0%, #ef4444 30%, #f97316 60%, #ef4444 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmer 6s linear infinite; }
        .ring-anim { stroke-dasharray: 283; animation: drawRing 2s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
      `}</style>

      {/* ═══ Page header strip ═══ */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">Health Tracker</h1>
              <p className="text-gray-500 text-xs">Your carnivore journey in one beautiful dashboard</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Hero ═══ */}
      <div className="relative">
        {/* Ambient blurred blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-40" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-40 -right-20 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
        <div className="pointer-events-none absolute top-80 left-1/3 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* LEFT — copy */}
            <div className="fade-slide" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold text-gray-700 mb-5 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Free · No credit card
              </div>
              <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.05] mb-5">
                Track everything <br />
                that <span className="shimmer-text">matters</span>
              </h2>
              <p className="text-lg text-gray-500 max-w-md mb-8">Macros, mood, weight, and elimination diet — beautifully visualized with progress rings, line charts, and daily goals.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/signup" className="group px-8 py-3.5 bg-primary text-white rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
                  Start tracking free
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </a>
                <a href="/login" className="px-8 py-3.5 border border-gray-200 text-gray-700 bg-white rounded-xl font-semibold hover:border-primary hover:text-primary transition-colors">Log In</a>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {['🥩', '🧈', '🥓', '🥚'].map((e, i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-white border-2 border-gray-50 flex items-center justify-center shadow-sm text-sm fade-slide" style={{ animationDelay: `${0.3 + i * 0.08}s` }}>{e}</div>
                  ))}
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-gray-800">10,000+ carnivores</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500"><span className="text-yellow-500">★★★★★</span> 4.9 avg rating</div>
                </div>
              </div>
            </div>

            {/* RIGHT — animated mock dashboard */}
            <div className="fade-slide" style={{ animationDelay: '0.3s' }}>
              <div className="relative">
                {/* Floating badges */}
                <div className="absolute -top-6 -left-6 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float-alt">
                  <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center text-green-600 text-lg">✓</div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Goal hit</div>
                    <div className="text-sm font-bold text-gray-800">150g protein</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Streak</div>
                    <div className="text-sm font-bold text-gray-800">12 days</div>
                  </div>
                </div>

                {/* Main dashboard card */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 p-6 relative overflow-hidden">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-xs text-gray-400 font-medium">Today · Mon Apr 17</div>
                      <div className="text-lg font-bold text-gray-800">Daily Summary</div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </div>
                  </div>

                  {/* Macro rings mock */}
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {[
                      { label: 'Protein', val: '142g', pct: 94, color: '#ef4444', emoji: '🥩' },
                      { label: 'Fat', val: '88g', pct: 73, color: '#f59e0b', emoji: '🧈' },
                      { label: 'Calories', val: '1842', pct: 81, color: '#f97316', emoji: '🔥' },
                    ].map((m, i) => {
                      const r = 36, c = 2 * Math.PI * r, off = c - (m.pct / 100) * c;
                      return (
                        <div key={m.label} className="text-center">
                          <div className="relative inline-flex items-center justify-center" style={{ width: 88, height: 88 }}>
                            <svg width={88} height={88} className="-rotate-90">
                              <circle cx={44} cy={44} r={r} stroke="#f3f4f6" strokeWidth={7} fill="none" />
                              <circle cx={44} cy={44} r={r} stroke={m.color} strokeWidth={7} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ animation: `drawRing 1.6s cubic-bezier(0.4,0,0.2,1) ${0.5 + i * 0.15}s backwards` }} />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-base">{m.emoji}</span>
                              <span className="text-xs font-bold text-gray-800">{m.val}</span>
                            </div>
                          </div>
                          <div className="text-[10px] font-semibold text-gray-500 mt-1">{m.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Weight mini-chart mock */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-600">⚖️ Weight · 30d</span>
                      <span className="text-xs font-bold text-green-600">↓ 3.2 kg</span>
                    </div>
                    <svg viewBox="0 0 200 40" className="w-full" preserveAspectRatio="none" style={{ height: 40 }}>
                      <defs>
                        <linearGradient id="wgL" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M 0 15 L 30 18 L 60 12 L 90 20 L 120 14 L 150 22 L 180 18 L 200 25 L 200 40 L 0 40 Z" fill="url(#wgL)" style={{ animation: 'fadeSlide 1.2s ease-out 0.6s backwards' }} />
                      <path d="M 0 15 L 30 18 L 60 12 L 90 20 L 120 14 L 150 22 L 180 18 L 200 25" stroke="#3b82f6" strokeWidth={2} fill="none" strokeLinecap="round" strokeDasharray={500} strokeDashoffset={500} style={{ animation: 'drawRing 2s ease-out 0.8s forwards' }} />
                    </svg>
                  </div>

                  {/* Mood bars mock */}
                  <div className="flex items-end justify-between gap-1 h-10">
                    {[4, 6, 5, 8, 7, 9, 8].map((h, i) => (
                      <div key={i} className="flex-1 bg-gradient-to-t from-purple-400 to-purple-300 rounded-sm origin-bottom" style={{ height: `${h * 10}%`, animation: `barGrow 0.8s cubic-bezier(0.4,0,0.2,1) ${0.9 + i * 0.08}s backwards` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll hint */}
          <div className="flex justify-center mt-16">
            <div className="flex flex-col items-center gap-2 text-gray-400 animate-bounce" style={{ animationDuration: '2s' }}>
              <span className="text-[11px] font-semibold uppercase tracking-wider">See features</span>
              <span>↓</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Feature tiles ═══ */}
      <div className="relative bg-white border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center max-w-xl mx-auto mb-10 fade-slide">
            <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">What you get</div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">Four pillars of your health</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: '🍖', title: 'Macros', desc: 'Protein, fat & calories', tint: 'bg-red-50 text-red-600', hover: 'hover:border-red-200' },
              { icon: '😊', title: 'Mood', desc: 'How you feel daily', tint: 'bg-purple-50 text-purple-600', hover: 'hover:border-purple-200' },
              { icon: '⚖️', title: 'Weight', desc: 'Progress over time', tint: 'bg-blue-50 text-blue-600', hover: 'hover:border-blue-200' },
              { icon: '🚫', title: 'Elimination', desc: 'Reintroductions', tint: 'bg-orange-50 text-orange-600', hover: 'hover:border-orange-200' },
            ].map((f, i) => (
              <div key={f.title} className={`group bg-white rounded-2xl border border-gray-100 p-5 text-center shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-default fade-slide ${f.hover}`} style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
                <div className={`w-14 h-14 mx-auto mb-3 rounded-2xl ${f.tint} flex items-center justify-center text-2xl group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300`}>{f.icon}</div>
                <h3 className="font-bold text-sm text-gray-800 mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ Feature rows — alternating ═══ */}
      <div className="max-w-6xl mx-auto px-6 py-20 space-y-20">
        {[
          {
            kicker: 'Calendar view', title: 'See your month at a glance',
            desc: 'Every day shows color dots for what you logged. Tap any day to jump straight to its entries — no scrolling, no searching.',
            mock: 'calendar',
          },
          {
            kicker: 'Monthly stats', title: 'Charts that reveal patterns',
            desc: 'Line charts for weight, bar charts for mood distribution, and percentage breakdowns of your most frequent states. See trends, not just data.',
            mock: 'stats', reverse: true,
          },
          {
            kicker: 'Daily goals', title: 'Progress rings that motivate',
            desc: 'Set protein, fat, and calorie targets. Watch them fill as you log meals. Gentle, data-driven motivation — no streaks guilt.',
            mock: 'rings',
          },
        ].map((row, i) => (
          <div key={i} className={`grid md:grid-cols-2 gap-10 items-center ${row.reverse ? 'md:[direction:rtl]' : ''}`}>
            <div className="fade-slide md:[direction:ltr]" style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
              <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">{row.kicker}</div>
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight mb-3">{row.title}</h3>
              <p className="text-gray-500 leading-relaxed">{row.desc}</p>
            </div>

            <div className="md:[direction:ltr]">
              {/* Mock visualizations */}
              {row.mock === 'calendar' && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-5 max-w-sm mx-auto">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-bold text-gray-800">April 2026</div>
                    <div className="flex gap-1">
                      <button className="w-6 h-6 rounded-lg bg-gray-50 text-gray-400 text-xs">‹</button>
                      <button className="w-6 h-6 rounded-lg bg-gray-50 text-gray-400 text-xs">›</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => <div key={idx} className="text-center text-[10px] font-bold text-gray-400 py-1">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 30 }).map((_, idx) => {
                      const dots = idx % 3 === 0 ? ['bg-red-400', 'bg-purple-400'] : idx % 4 === 0 ? ['bg-blue-400'] : idx % 5 === 0 ? ['bg-red-400', 'bg-purple-400', 'bg-blue-400'] : [];
                      const today = idx === 16;
                      return (
                        <div key={idx} className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs ${today ? 'bg-gradient-to-br from-primary to-orange-500 text-white font-bold shadow-md' : dots.length ? 'bg-gray-50 text-gray-700' : 'text-gray-300'} fade-slide`} style={{ animationDelay: `${idx * 0.02}s` }}>
                          <span>{idx + 1}</span>
                          {dots.length > 0 && !today && (
                            <div className="absolute bottom-1 flex gap-0.5">
                              {dots.map((d, di) => <span key={di} className={`w-1 h-1 rounded-full ${d}`} />)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {row.mock === 'stats' && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-5 max-w-sm mx-auto space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-bold text-gray-800">⚖️ Weight trend</span>
                      <span className="text-xs font-bold text-green-600">↓ 2.8 kg</span>
                    </div>
                    <svg viewBox="0 0 300 80" className="w-full" preserveAspectRatio="none" style={{ height: 80 }}>
                      <defs>
                        <linearGradient id="wg2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M 0 20 Q 40 25 80 18 T 160 30 T 240 25 T 300 45 L 300 80 L 0 80 Z" fill="url(#wg2)" />
                      <path d="M 0 20 Q 40 25 80 18 T 160 30 T 240 25 T 300 45" stroke="#3b82f6" strokeWidth={2.5} fill="none" strokeLinecap="round" />
                      {[0, 80, 160, 240, 300].map((x, i) => (
                        <g key={i}>
                          <circle cx={x} cy={i === 0 ? 20 : i === 1 ? 18 : i === 2 ? 30 : i === 3 ? 25 : 45} r={4} fill="#3b82f6" />
                          <circle cx={x} cy={i === 0 ? 20 : i === 1 ? 18 : i === 2 ? 30 : i === 3 ? 25 : 45} r={2} fill="white" />
                        </g>
                      ))}
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-2">😊 Mood</div>
                    <div className="space-y-1.5">
                      {[
                        { m: '🤩 Thriving', p: 40 },
                        { m: '😊 Energized', p: 28 },
                        { m: '🙂 Balanced', p: 20 },
                        { m: '😶 Neutral', p: 12 },
                      ].map((r2, ri) => (
                        <div key={ri} className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-600 w-20">{r2.m}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-purple-500 to-violet-500 rounded-full" style={{ width: `${r2.p}%`, animation: `fadeSlide 0.8s ease-out ${ri * 0.15}s backwards` }} />
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 w-8 text-right">{r2.p}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {row.mock === 'rings' && (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-xl p-8 max-w-sm mx-auto">
                  <div className="text-center mb-6">
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Today's goals</div>
                    <div className="text-2xl font-bold text-gray-800 mt-1">93% complete 🎯</div>
                  </div>
                  <div className="relative flex items-center justify-center" style={{ width: 220, height: 220, margin: '0 auto' }}>
                    {[
                      { r: 95, color: '#ef4444', pct: 94, stroke: 10 },
                      { r: 75, color: '#f59e0b', pct: 73, stroke: 10 },
                      { r: 55, color: '#f97316', pct: 81, stroke: 10 },
                    ].map((ring, ri) => {
                      const c = 2 * Math.PI * ring.r;
                      const off = c - (ring.pct / 100) * c;
                      return (
                        <svg key={ri} width={220} height={220} className="absolute inset-0 -rotate-90">
                          <circle cx={110} cy={110} r={ring.r} stroke="#f3f4f6" strokeWidth={ring.stroke} fill="none" />
                          <circle cx={110} cy={110} r={ring.r} stroke={ring.color} strokeWidth={ring.stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ animation: `drawRing 2s cubic-bezier(0.4,0,0.2,1) ${ri * 0.2}s backwards` }} />
                        </svg>
                      );
                    })}
                    <div className="text-center z-10">
                      <div className="text-3xl">🔥</div>
                      <div className="text-xs font-bold text-gray-500 mt-1">3/3 hit</div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 mt-6 text-[11px] font-semibold">
                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Protein</div>
                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Fat</div>
                    <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-500" /> Kcal</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ═══ Final CTA ═══ */}
      <div className="relative bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center fade-slide">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 rounded-full px-3 py-1 text-xs font-semibold mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Free forever
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-3">Start tracking in seconds</h2>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">Sign up free to log your daily macros, mood, weight, and diet progress.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href="/signup" className="group px-8 py-3.5 bg-primary text-white rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
              Create free account
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
            <a href="/login" className="px-8 py-3.5 border border-gray-200 text-gray-700 bg-white rounded-xl font-semibold hover:border-primary hover:text-primary transition-colors">Log In</a>
          </div>
        </div>
      </div>
    </div>
  );

  /* ───── Derived data ───── */
  const macroLogs = dayLogs.filter(l => l.log_type === 'Macro Log');
  const totalProtein = macroLogs.reduce((s, l) => s + (l.protein || 0), 0);
  const totalFat = macroLogs.reduce((s, l) => s + (l.fat || 0), 0);
  const totalCalories = macroLogs.reduce((s, l) => s + (l.calories || 0), 0);
  const proteinPct = goals?.daily_protein_goal ? Math.min((totalProtein / goals.daily_protein_goal) * 100, 100) : 0;
  const fatPct = goals?.daily_fat_goal ? Math.min((totalFat / goals.daily_fat_goal) * 100, 100) : 0;
  const caloriePct = goals?.daily_calorie_goal ? Math.min((totalCalories / goals.daily_calorie_goal) * 100, 100) : 0;

  const [year, month] = currentMonth.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const getDateLogs = (day: number) => monthLogs.filter(l => l.date === `${currentMonth}-${String(day).padStart(2, '0')}`);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const allMacroLogs = monthLogs.filter(l => l.log_type === 'Macro Log');
  const allMoodLogs = monthLogs.filter(l => l.log_type === 'Mood Log');
  const allWeightLogs = monthLogs.filter(l => l.log_type === 'Weight Track');

  const moodOfDay = dayLogs.find(l => l.log_type === 'Mood Log');
  const weightOfDay = dayLogs.find(l => l.log_type === 'Weight Track');

  const logButtons: { type: string; IconC: IconType; label: string; grad: string }[] = [
    { type: 'Macro Log',       IconC: FaDrumstickBite, label: 'Macros',     grad: 'from-red-500 to-rose-600' },
    { type: 'Eliminated Item', IconC: FaBan,           label: 'Eliminated', grad: 'from-orange-500 to-amber-600' },
    { type: 'Added Item',      IconC: FaPlus,          label: 'Added',      grad: 'from-green-500 to-emerald-600' },
    { type: 'Mood Log',        IconC: FaSmile,         label: 'Mood',       grad: 'from-purple-500 to-violet-600' },
    { type: 'Weight Track',    IconC: FaWeight,        label: 'Weight',     grad: 'from-blue-500 to-sky-600' },
    { type: 'Daily Note',      IconC: FaStickyNote,    label: 'Note',       grad: 'from-yellow-500 to-amber-500' },
    { type: 'Period Log',      IconC: FaTint,          label: 'Period',     grad: 'from-pink-500 to-rose-600' },
  ];

  const dateObj = new Date(selectedDate + 'T00:00:00');
  const today = new Date().toISOString().slice(0, 10);
  const isToday = selectedDate === today;
  const prettyDate = dateObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const shiftDate = (delta: number) => {
    const d = new Date(selectedDate + 'T00:00:00'); d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  /* ───── Weight chart (SVG line) ───── */
  const weightChart = (() => {
    if (allWeightLogs.length < 1) return null;
    const sorted = [...allWeightLogs].sort((a: any, b: any) => a.date.localeCompare(b.date));
    const vals = sorted.map((l: any) => l.weight_kg);
    const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
    const W = 600, H = 140, P = 20;
    const points = sorted.map((l: any, i: number) => {
      const x = sorted.length === 1 ? W / 2 : P + (i / (sorted.length - 1)) * (W - P * 2);
      const y = H - P - ((l.weight_kg - min) / range) * (H - P * 2);
      return { x, y, v: l.weight_kg, d: l.date };
    });
    const path = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
    const area = `${path} L ${points[points.length - 1].x} ${H - P} L ${points[0].x} ${H - P} Z`;
    return { W, H, path, area, points, min, max };
  })();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Helmet>
        <title>Tracker — EatMeat</title>
        <meta name="description" content="Your personal health tracker — log macros, weight, mood, and elimination diet." />
      </Helmet>
      {/* ── Page header strip ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4 md:py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 rounded-full px-2.5 py-0.5 text-[11px] font-semibold mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {isToday ? 'Today' : prettyDate}
              </div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">Health Tracker</h1>
              <p className="text-gray-500 text-xs">Macros · mood · weight · elimination</p>
            </div>
            <div className="flex gap-2">
              <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-xl px-3 py-2 text-xs">
                <FaFire className="text-orange-500" />
                <div className="leading-none">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">Streak</div>
                  <div className="font-bold text-orange-700 mt-0.5">{streak} <span className="font-normal text-gray-400">day{streak !== 1 ? 's' : ''}</span></div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs">
                <FaCalendarDay className="text-blue-500" />
                <div className="leading-none">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">This month</div>
                  <div className="font-bold text-blue-700 mt-0.5">{daysTrackedThisMonth} <span className="font-normal text-gray-400">day{daysTrackedThisMonth !== 1 ? 's' : ''}</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats on neutral background */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            <div className="bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Entries</div>
              <div className="text-xl font-bold text-gray-800 mt-0.5">{dayLogs.length}</div>
            </div>
            <div className="bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Calories</div>
              <div className="text-xl font-bold text-gray-800 mt-0.5">{totalCalories.toFixed(0)}</div>
            </div>
            <div className={`rounded-xl px-3 py-2.5 border ${moodOfDay ? 'bg-purple-50 border-purple-100' : 'bg-gray-50 border-gray-100'}`}>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Mood</div>
              <div className="text-xl font-bold text-gray-800 mt-0.5">{moodOfDay ? (moodEmojis[moodOfDay.mood_rating] || '😶') : '—'}</div>
            </div>
            <div className={`rounded-xl px-3 py-2.5 border ${weightOfDay ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-100'}`}>
              <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Weight</div>
              <div className="text-xl font-bold text-gray-800 mt-0.5">
                {weightOfDay ? <>{weightOfDay.weight_kg?.toFixed(1)}<span className="text-xs font-normal text-gray-500">kg</span></> : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6">
        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-1.5 flex gap-1 mb-6">
          {([
            { id: 'log',      label: 'Log',      IconC: FaClipboardList },
            { id: 'calendar', label: 'Calendar', IconC: FaCalendarDay },
            { id: 'stats',    label: 'Stats',    IconC: FaChartBar },
          ] as const).map(tab => {
            const TIcon = tab.IconC;
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${active ? 'bg-gradient-to-br from-primary to-orange-500 text-white shadow-md shadow-primary/30' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                <TIcon className={active ? 'text-white' : 'text-gray-400'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Log Tab ── */}
        {activeTab === 'log' && (
          <div className="space-y-5">
            {/* Date navigator */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center justify-between">
              <button onClick={() => shiftDate(-1)} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center transition-colors" aria-label="Previous day"><FaChevronLeft /></button>
              <div className="flex items-center gap-3">
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium bg-gray-50 focus:bg-white focus:border-primary focus:outline-none transition-colors" />
                {!isToday && <button onClick={() => setSelectedDate(today)} className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full font-semibold hover:bg-primary/20 transition-colors">Today</button>}
              </div>
              <button onClick={() => shiftDate(1)} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center transition-colors" aria-label="Next day"><FaChevronRight /></button>
            </div>

            {/* Macro rings */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="font-bold text-gray-800">Daily Macros</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{macroLogs.length} meal{macroLogs.length !== 1 ? 's' : ''} logged</p>
                </div>
                <button onClick={() => setEditGoals(!editGoals)} className="text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors">
                  {editGoals ? 'Cancel' : goals ? 'Edit Goals' : 'Set Goals'}
                </button>
              </div>
              {editGoals ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-500 font-medium">Protein (g)</label><input type="number" value={goalProtein} onChange={e => setGoalProtein(e.target.value)} className="w-full px-3 py-2 mt-1 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" placeholder="150" /></div>
                    <div><label className="text-xs text-gray-500 font-medium">Fat (g)</label><input type="number" value={goalFat} onChange={e => setGoalFat(e.target.value)} className="w-full px-3 py-2 mt-1 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" placeholder="120" /></div>
                    <div><label className="text-xs text-gray-500 font-medium">Calories</label><input type="number" value={goalCalories} onChange={e => setGoalCalories(e.target.value)} className="w-full px-3 py-2 mt-1 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" placeholder="2000" /></div>
                  </div>
                  <button onClick={handleSaveGoals} className="px-6 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors">Save Goals</button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {([
                    { label: 'Protein',  val: totalProtein,  goal: goals?.daily_protein_goal,  pct: proteinPct, color: '#ef4444', IconC: FaDrumstickBite, iconClass: 'text-red-400' },
                    { label: 'Fat',      val: totalFat,      goal: goals?.daily_fat_goal,      pct: fatPct,     color: '#f59e0b', IconC: FaButter,        iconClass: 'text-amber-400' },
                    { label: 'Calories', val: totalCalories, goal: goals?.daily_calorie_goal, pct: caloriePct, color: '#f97316', IconC: FaFire,          iconClass: 'text-orange-400' },
                  ] as const).map(m => {
                    const RIcon = m.IconC;
                    return (
                      <div key={m.label} className="text-center">
                        <Ring pct={m.pct} color={m.color} size={90} stroke={8}>
                          <div>
                            <RIcon className={`mx-auto ${m.iconClass}`} />
                            <div className="text-lg font-bold text-gray-800 leading-tight">{m.val.toFixed(0)}</div>
                            <div className="text-[9px] text-gray-400">{m.goal ? `/${m.goal}` : 'no goal'}</div>
                          </div>
                        </Ring>
                        <div className="text-xs font-semibold text-gray-600 mt-2">{m.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick-add grid */}
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">Add Entry</h3>
              <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                {logButtons.map(btn => {
                  const active = addType === btn.type;
                  const BIcon = btn.IconC;
                  return (
                    <button key={btn.type} onClick={() => { resetForm(); setAddType(active ? null : btn.type); }}
                      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-2xl text-xs font-semibold transition-all active:scale-95 ${active ? `bg-gradient-to-br ${btn.grad} text-white shadow-lg scale-105` : 'bg-white border border-gray-100 text-gray-600 hover:border-gray-300 hover:shadow-sm'}`}>
                      <BIcon className="text-xl" />
                      <span className="text-[11px]">{btn.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Add form */}
            {addType && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4 animate-[fadeIn_0.2s_ease-out]">
                <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
                  <LogTypeIcon type={addType} className="text-xl text-gray-600" />
                  <h3 className="font-bold text-gray-800">{addType}</h3>
                </div>

                {(addType === 'Eliminated Item' || addType === 'Added Item') && (
                  <>
                    <div><label className="text-xs font-medium text-gray-500 mb-1 block">Item Name</label><input value={itemName} onChange={e => setItemName(e.target.value)} placeholder="e.g. Dairy, Eggs, Chicken" className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" /></div>
                    <div><label className="text-xs font-medium text-gray-500 mb-1 block">Notes (optional)</label><textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any observations..." className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none resize-none" rows={2} /></div>
                  </>
                )}

                {addType === 'Daily Note' && (
                  <div><label className="text-xs font-medium text-gray-500 mb-1 block">Your Note</label><textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Record your daily observations..." className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none resize-none" rows={4} /></div>
                )}

                {addType === 'Mood Log' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-2 block">How do you feel?</label>
                      <div className="grid grid-cols-5 gap-2">
                        {moodLevels.map(m => (
                          <button key={m} onClick={() => setMoodRating(m)}
                            className={`flex flex-col items-center gap-1 py-2 rounded-xl text-[11px] font-medium transition-all ${moodRating === m ? 'bg-primary text-white shadow-md scale-105' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>
                            <span className="text-xl">{moodEmojis[m]}</span>
                            <span className="truncate w-full text-center">{m}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 flex justify-between"><span>Energy Level</span><span className="text-primary font-bold">{energyLevel}/10</span></label>
                      <input type="range" min={1} max={10} value={energyLevel} onChange={e => setEnergyLevel(parseInt(e.target.value))} className="w-full accent-primary h-2" />
                    </div>
                    <div><label className="text-xs font-medium text-gray-500 mb-1 block">Notes (optional)</label><textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none resize-none" rows={2} /></div>
                  </>
                )}

                {addType === 'Weight Track' && (
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Weight</label>
                      <input type="number" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder={weightUnit === 'kg' ? '70.5' : '155.4'} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Unit</label>
                      <div className="flex bg-gray-100 rounded-lg p-0.5">
                        {['kg', 'lb'].map(u => (
                          <button key={u} onClick={() => setWeightUnit(u)} className={`px-4 py-2 text-sm font-semibold rounded-md transition-all ${weightUnit === u ? 'bg-white shadow text-primary' : 'text-gray-500'}`}>{u}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {addType === 'Period Log' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-2 block">Flow</label>
                      <div className="grid grid-cols-4 gap-2">
                        {periodFlows.map(f => (
                          <button key={f} onClick={() => setPeriodFlow(periodFlow === f ? '' : f)}
                            className={`py-2 rounded-xl text-xs font-semibold transition-all ${periodFlow === f ? 'bg-gradient-to-br from-pink-500 to-rose-600 text-white shadow-md' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}>{f}</button>
                        ))}
                      </div>
                    </div>
                    <label className="flex items-center gap-2.5 text-sm text-gray-700 bg-pink-50 rounded-lg px-3 py-2.5 cursor-pointer">
                      <input type="checkbox" checked={startPeriod} onChange={e => setStartPeriod(e.target.checked)} className="accent-pink-500 w-4 h-4" /> Mark as period start
                    </label>
                    <div><label className="text-xs font-medium text-gray-500 mb-1 block">Notes (optional)</label><textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none resize-none" rows={2} /></div>
                  </>
                )}

                {addType === 'Macro Log' && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">Meal</label>
                      <div className="grid grid-cols-4 gap-2">
                        {mealTypes.map(t => (
                          <button key={t} onClick={() => setMacroMealType(t)} className={`py-2 rounded-xl text-xs font-semibold transition-all ${macroMealType === t ? 'bg-primary text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}>{t}</button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><label className="text-xs font-medium text-red-600 mb-1 inline-flex items-center gap-1.5"><FaDrumstickBite /> Protein (g)</label><input type="number" value={macroProtein} onChange={e => setMacroProtein(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-red-500 focus:outline-none" /></div>
                      <div><label className="text-xs font-medium text-yellow-600 mb-1 inline-flex items-center gap-1.5"><FaButter /> Fat (g)</label><input type="number" value={macroFat} onChange={e => setMacroFat(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-yellow-500 focus:outline-none" /></div>
                      <div><label className="text-xs font-medium text-orange-600 mb-1 inline-flex items-center gap-1.5"><FaFire /> Calories</label><input type="number" value={macroCalories} onChange={e => setMacroCalories(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-orange-500 focus:outline-none" /></div>
                    </div>
                    <div><label className="text-xs font-medium text-gray-500 mb-1 block">Notes (optional)</label><input value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none" /></div>
                  </>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button onClick={handleAddEntry} disabled={saving} className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors shadow-md shadow-primary/20">
                    {saving ? 'Saving…' : 'Save Entry'}
                  </button>
                  <button onClick={resetForm} className="px-6 py-2.5 text-gray-500 text-sm font-medium hover:bg-gray-50 rounded-xl transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {/* Day entries list */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">Entries</h3>
                <span className="text-xs font-semibold text-gray-400">{dayLogs.length} total</span>
              </div>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}
                </div>
              ) : dayLogs.length === 0 ? (
                <div className="py-10 text-center">
                  <FaClipboardList className="text-4xl mx-auto mb-3 text-gray-200" />
                  <p className="text-gray-500 text-sm font-medium">No entries for this day</p>
                  <p className="text-gray-400 text-xs mt-1">Pick a quick-add button above to log macros, mood, weight, or notes.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {dayLogs.map(log => {
                    const c = logTypeColors[log.log_type] || logTypeColors['Daily Note'];
                    return (
                      <div key={log.id} className={`group flex items-start gap-3 p-3.5 ${c.bg} rounded-xl hover:ring-2 ${c.ring} transition-all`}>
                        <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm ${c.text}`}>
                          <LogTypeIcon type={log.log_type} className="text-lg" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs font-bold uppercase tracking-wider ${c.text}`}>{log.log_type}</div>
                          <div className="text-sm text-gray-800 mt-0.5">
                            {log.log_type === 'Macro Log' && <>{log.meal_type} · <span className="text-red-600 font-semibold">P:{log.protein}g</span> · <span className="text-yellow-600 font-semibold">F:{log.fat}g</span> · <span className="text-orange-600 font-semibold">{log.calories}kcal</span></>}
                            {log.log_type === 'Mood Log' && <>{moodEmojis[log.mood_rating]} {log.mood_rating} · Energy {log.energy_level}/10</>}
                            {log.log_type === 'Weight Track' && <span className="font-semibold">{log.weight_kg?.toFixed(1)} kg</span>}
                            {(log.log_type === 'Eliminated Item' || log.log_type === 'Added Item') && <span className="font-semibold">{log.Item_name}</span>}
                            {log.log_type === 'Period Log' && <>{log.Item_name || 'Logged'} {log.period_start_date && <span className="text-pink-600 font-semibold">· Period start</span>}</>}
                            {log.log_type === 'Daily Note' && <span className="italic">"{log.notes}"</span>}
                          </div>
                          {log.notes && log.log_type !== 'Daily Note' && <p className="text-xs text-gray-500 mt-1 italic">"{log.notes}"</p>}
                        </div>
                        <button onClick={() => handleDelete(log)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg w-8 h-8 flex items-center justify-center transition-all flex-shrink-0" aria-label="Delete"><FaTrashAlt /></button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Calendar Tab ── */}
        {activeTab === 'calendar' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center justify-between">
              <button onClick={() => { const d = new Date(year, month - 2, 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center transition-colors"><FaChevronLeft /></button>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800">{monthNames[month - 1]}</div>
                <div className="text-xs text-gray-400">{year}</div>
              </div>
              <button onClick={() => { const d = new Date(year, month, 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center transition-colors"><FaChevronRight /></button>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                  <div key={d} className={`text-center text-[10px] font-bold py-1 uppercase tracking-wider ${i === 0 || i === 6 ? 'text-gray-300' : 'text-gray-400'}`}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                {calendarDays.map(day => {
                  const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
                  const logs = getDateLogs(day);
                  const isSelected = dateStr === selectedDate;
                  const todayFlag = dateStr === today;
                  const types = Array.from(new Set(logs.map((l: any) => l.log_type))) as string[];
                  // heatmap intensity: 0 → 4
                  const intensity = Math.min(4, logs.length);
                  const heatBg = ['', 'bg-orange-50', 'bg-orange-100', 'bg-orange-200', 'bg-orange-300'][intensity];
                  const heatText = intensity >= 3 ? 'text-orange-900' : intensity >= 1 ? 'text-orange-800' : 'text-gray-400';
                  return (
                    <button key={day} onClick={() => { setSelectedDate(dateStr); setActiveTab('log'); }}
                      title={`${dateStr} · ${logs.length} entr${logs.length === 1 ? 'y' : 'ies'}`}
                      className={`relative aspect-square rounded-xl text-sm flex flex-col items-center justify-center transition-all
                        ${isSelected
                          ? 'bg-gradient-to-br from-primary to-orange-500 text-white shadow-md scale-105'
                          : todayFlag
                            ? 'bg-white text-primary font-bold ring-2 ring-primary/40'
                            : intensity > 0
                              ? `${heatBg} ${heatText} hover:brightness-95`
                              : 'hover:bg-gray-50 text-gray-300'
                        }`}>
                      <span className={`text-sm font-semibold ${isSelected ? 'text-white' : ''}`}>{day}</span>
                      {types.length > 0 && !isSelected && (
                        <div className="absolute bottom-1 flex gap-0.5">
                          {types.slice(0, 4).map((t, i) => (
                            <span key={i} className={`w-1 h-1 rounded-full ${logTypeColors[t]?.dot || 'bg-gray-400'}`} />
                          ))}
                        </div>
                      )}
                      {isSelected && logs.length > 0 && (
                        <div className="absolute bottom-1 text-[9px] font-semibold opacity-80">{logs.length}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {/* heatmap legend */}
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
                <span>Activity</span>
                <div className="inline-flex items-center gap-1">
                  <span>Less</span>
                  <span className="w-3 h-3 rounded bg-gray-100" />
                  <span className="w-3 h-3 rounded bg-orange-50" />
                  <span className="w-3 h-3 rounded bg-orange-100" />
                  <span className="w-3 h-3 rounded bg-orange-200" />
                  <span className="w-3 h-3 rounded bg-orange-300" />
                  <span>More</span>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">Legend</div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(logTypeColors).map(([type, c]) => (
                  <div key={type} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                    <span className="text-xs text-gray-500 inline-flex items-center gap-1"><LogTypeIcon type={type} className={`${c.text}`} /> {type.replace(' Log', '').replace(' Item', '').replace(' Track', '')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Stats Tab ── */}
        {activeTab === 'stats' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center justify-between">
              <button onClick={() => { const d = new Date(year, month - 2, 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center"><FaChevronLeft /></button>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-800">{monthNames[month - 1]}</div>
                <div className="text-xs text-gray-400">{year}</div>
              </div>
              <button onClick={() => { const d = new Date(year, month, 1); setCurrentMonth(d.toISOString().slice(0, 7)); }} className="w-10 h-10 rounded-xl hover:bg-gray-50 text-gray-500 flex items-center justify-center"><FaChevronRight /></button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {([
                { label: 'Days Tracked', value: new Set(allMacroLogs.map((l: any) => l.date)).size,                                                                                                                                                                                                                                                                                                                                                                                                grad: 'from-primary to-orange-500',    IconC: FaCalendarDay },
                { label: 'Avg Protein',  value: `${allMacroLogs.length > 0 ? (allMacroLogs.reduce((s: number, l: any) => s + (l.protein || 0), 0) / Math.max(new Set(allMacroLogs.map((l: any) => l.date)).size, 1)).toFixed(0) : 0}g`, grad: 'from-red-500 to-rose-600',      IconC: FaDrumstickBite },
                { label: 'Mood Logs',    value: allMoodLogs.length,                                                                                                                                                                                                                                                                                                                                                                                                                                grad: 'from-purple-500 to-violet-600', IconC: FaSmile },
                { label: 'Weight Logs',  value: allWeightLogs.length,                                                                                                                                                                                                                                                                                                                                                                                                                              grad: 'from-blue-500 to-sky-600',      IconC: FaWeight },
              ] as const).map(s => {
                const SIcon = s.IconC;
                return (
                  <div key={s.label} className={`relative overflow-hidden bg-gradient-to-br ${s.grad} text-white rounded-2xl p-4 shadow-md`}>
                    <SIcon className="absolute -top-3 -right-3 text-5xl opacity-20" />
                    <p className="text-3xl font-extrabold relative">{s.value}</p>
                    <p className="text-xs text-white/80 mt-1 font-medium relative">{s.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Weight line chart */}
            {weightChart && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <h3 className="font-bold text-gray-800 inline-flex items-center gap-2"><FaWeight className="text-blue-500" /> Weight Trend</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{allWeightLogs.length} log{allWeightLogs.length !== 1 ? 's' : ''} this month</p>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">{weightChart.min.toFixed(1)} – {weightChart.max.toFixed(1)} kg</div>
                    {weightChart.points.length >= 2 && (
                      <div className={`text-xs font-semibold inline-flex items-center gap-1 ${weightChart.points[weightChart.points.length - 1].v < weightChart.points[0].v ? 'text-green-600' : weightChart.points[weightChart.points.length - 1].v > weightChart.points[0].v ? 'text-red-600' : 'text-gray-500'}`}>
                        {weightChart.points[weightChart.points.length - 1].v < weightChart.points[0].v ? <FaArrowDown /> : weightChart.points[weightChart.points.length - 1].v > weightChart.points[0].v ? <FaArrowUp /> : <span>—</span>}
                        <span>{Math.abs(weightChart.points[weightChart.points.length - 1].v - weightChart.points[0].v).toFixed(1)} kg</span>
                      </div>
                    )}
                  </div>
                </div>
                <svg viewBox={`0 0 ${weightChart.W} ${weightChart.H}`} className="w-full" preserveAspectRatio="none" style={{ height: 160 }}>
                  <defs>
                    <linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={weightChart.area} fill="url(#wg)" />
                  <path d={weightChart.path} stroke="#3b82f6" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  {weightChart.points.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r={4} fill="#3b82f6" />
                      <circle cx={p.x} cy={p.y} r={2} fill="white" />
                    </g>
                  ))}
                </svg>
              </div>
            )}

            {/* 30-day trends — Calories + Mood (bars / sparkline) */}
            {(() => {
              const calMax = Math.max(1, ...trendData.caloriesByDay.map((d) => d.value));
              const calGoal = goals?.daily_calorie_goal || 0;
              return (
                <div className="grid md:grid-cols-2 gap-3">
                  {/* Calories 30d */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-800 inline-flex items-center gap-2"><FaFire className="text-orange-500" /> Calories · 30d</h3>
                      {calGoal > 0 && <span className="text-[11px] text-gray-400">goal {calGoal}</span>}
                    </div>
                    <div className="flex items-end gap-0.5 h-24">
                      {trendData.caloriesByDay.map((d) => {
                        const h = (d.value / calMax) * 100;
                        const hit = calGoal > 0 && d.value >= calGoal;
                        return (
                          <div
                            key={d.iso}
                            title={`${d.iso}: ${d.value.toFixed(0)} kcal`}
                            className={`flex-1 rounded-sm transition-all ${d.value > 0 ? (hit ? 'bg-green-400' : 'bg-orange-300 hover:bg-orange-400') : 'bg-gray-100'}`}
                            style={{ height: `${Math.max(h, 4)}%` }}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                      <span>30 days ago</span><span>Today</span>
                    </div>
                  </div>

                  {/* Mood 30d (sparkline) */}
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-800 inline-flex items-center gap-2"><FaSmile className="text-purple-500" /> Mood · 30d</h3>
                      <span className="text-[11px] text-gray-400">1–10 scale</span>
                    </div>
                    {(() => {
                      const W = 600, H = 70, P = 4;
                      const data = trendData.moodByDay;
                      const xs = data.map((_, i) => P + (i / Math.max(1, data.length - 1)) * (W - P * 2));
                      const ys = data.map((d) => H - P - ((d.value || 0) / 10) * (H - P * 2));
                      // skip points with value=0 (no mood log) for the path so it doesn't dip to bottom every day
                      let path = '';
                      let started = false;
                      data.forEach((d, i) => {
                        if (d.value === 0) { started = false; return; }
                        path += `${started ? 'L' : 'M'} ${xs[i]} ${ys[i]} `;
                        started = true;
                      });
                      return (
                        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 70 }}>
                          <line x1={P} y1={H / 2} x2={W - P} y2={H / 2} stroke="#f3f4f6" strokeDasharray="4 4" />
                          {path && <path d={path} stroke="#8b5cf6" strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />}
                          {data.map((d, i) => d.value > 0 ? <circle key={i} cx={xs[i]} cy={ys[i]} r={3} fill="#8b5cf6" /> : null)}
                        </svg>
                      );
                    })()}
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1.5">
                      <span>30 days ago</span><span>Today</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Mood distribution bars */}
            {allMoodLogs.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <h3 className="font-bold text-gray-800 mb-4 inline-flex items-center gap-2"><FaSmile className="text-purple-500" /> Mood Distribution</h3>
                <div className="space-y-2">
                  {Object.entries(allMoodLogs.reduce((acc: Record<string, number>, l: any) => { acc[l.mood_rating] = (acc[l.mood_rating] || 0) + 1; return acc; }, {}))
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .map(([mood, count]) => {
                      const pct = ((count as number) / allMoodLogs.length) * 100;
                      return (
                        <div key={mood} className="flex items-center gap-3">
                          <div className="flex items-center gap-2 w-28 flex-shrink-0">
                            <span className="text-lg">{moodEmojis[mood] || '😶'}</span>
                            <span className="text-xs text-gray-600 font-medium">{mood}</span>
                          </div>
                          <div className="flex-1 bg-gray-100 rounded-full h-6 relative overflow-hidden">
                            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full flex items-center justify-end pr-2 transition-all" style={{ width: `${pct}%` }}>
                              <span className="text-[10px] font-bold text-white">{count as number}</span>
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-gray-400 w-10 text-right">{pct.toFixed(0)}%</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {monthLogs.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
                <FaChartBar className="text-4xl mx-auto mb-3 text-gray-200" />
                <p className="text-gray-500 font-medium">No data for this month yet</p>
                <p className="text-gray-400 text-sm mt-1">Switch to the Log tab and add your first entry.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
